import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, Star, Award, Shield, Users, TrendingUp } from "lucide-react";
import { AuthorBio } from "./AuthorBio";

interface PEOProvider {
  name: string;
  rank: number;
  score: number;
  strengths: string[];
  bestFor: string;
  pricing: string;
  accreditation: string;
  pros: string[];
  cons: string[];
}

const TOP_PEOS: PEOProvider[] = [
  {
    name: "ADP TotalSource",
    rank: 1,
    score: 9.2,
    strengths: ["Technology Platform", "Brand Recognition", "Global Reach"],
    bestFor: "Mid-size companies (50-500 employees)",
    pricing: "$1,800-$2,400 per employee annually",
    accreditation: "ESAC Accredited",
    pros: [
      "Industry-leading technology platform",
      "Comprehensive benefits packages",
      "Strong compliance support",
      "24/7 customer service"
    ],
    cons: [
      "Higher pricing than some competitors",
      "Less flexibility for very small businesses",
      "Complex implementation process"
    ]
  },
  {
    name: "Insperity",
    rank: 2,
    score: 9.0,
    strengths: ["Customer Service", "Benefits Quality", "Industry Expertise"],
    bestFor: "Small to mid-size businesses (5-150 employees)",
    pricing: "$1,600-$2,200 per employee annually",
    accreditation: "ESAC Accredited",
    pros: [
      "Exceptional customer service ratings",
      "Strong benefits and retirement plans",
      "Dedicated HR business partners",
      "Industry-specific expertise"
    ],
    cons: [
      "Limited technology features compared to larger competitors",
      "Geographic limitations in some areas",
      "Minimum employee requirements"
    ]
  },
  {
    name: "TriNet",
    rank: 3,
    score: 8.8,
    strengths: ["Industry Specialization", "Benefits Access", "Compliance"],
    bestFor: "Small businesses and startups (2-50 employees)",
    pricing: "$1,500-$2,000 per employee annually",
    accreditation: "ESAC Accredited",
    pros: [
      "Industry-specific HR solutions",
      "Competitive benefits pricing",
      "Strong startup and tech company focus",
      "Flexible service options"
    ],
    cons: [
      "Limited customization options",
      "Technology platform needs improvement",
      "Inconsistent service quality reported"
    ]
  },
  {
    name: "Paychex PEO",
    rank: 4,
    score: 8.5,
    strengths: ["Small Business Focus", "Payroll Integration", "Local Support"],
    bestFor: "Very small businesses (2-25 employees)",
    pricing: "$1,400-$1,900 per employee annually",
    accreditation: "ESAC Accredited",
    pros: [
      "Excellent for very small businesses",
      "Seamless payroll integration",
      "Local branch support network",
      "Competitive pricing for small groups"
    ],
    cons: [
      "Limited enterprise features",
      "Benefits options not as robust",
      "Technology platform feels dated"
    ]
  },
  {
    name: "Justworks",
    rank: 5,
    score: 8.3,
    strengths: ["Modern Platform", "Transparency", "Startup Focus"],
    bestFor: "Startups and tech companies (1-50 employees)",
    pricing: "$1,200-$1,800 per employee annually",
    accreditation: "Not ESAC Accredited",
    pros: [
      "Modern, user-friendly platform",
      "Transparent pricing model",
      "Great for remote and distributed teams",
      "Quick implementation process"
    ],
    cons: [
      "Lacks ESAC accreditation",
      "Limited industry specialization",
      "Fewer compliance services than traditional PEOs"
    ]
  }
];

export const BestPEOComparison = () => {
  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold">Best PEO Companies 2025: Expert Rankings & Reviews</h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Our comprehensive analysis of the top Professional Employer Organizations, 
          ranked by service quality, pricing, and customer satisfaction.
        </p>
        <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Award className="h-4 w-4 text-primary" />
            <span>Expert Reviewed</span>
          </div>
          <div className="flex items-center gap-1">
            <Shield className="h-4 w-4 text-primary" />
            <span>Unbiased Analysis</span>
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span>Updated January 2025</span>
          </div>
        </div>
      </div>

      {/* Rankings */}
      <div className="space-y-6">
        {TOP_PEOS.map((peo) => (
          <Card key={peo.name} className="relative overflow-hidden">
            {peo.rank <= 3 && (
              <div className="absolute top-4 right-4">
                <Badge variant="default" className="bg-primary">
                  <Star className="h-3 w-3 mr-1" />
                  Top {peo.rank}
                </Badge>
              </div>
            )}
            
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold">
                      {peo.rank}
                    </div>
                    <div>
                      <CardTitle className="text-2xl">{peo.name}</CardTitle>
                      <CardDescription className="text-base">
                        Best for: {peo.bestFor}
                      </CardDescription>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <span className="text-2xl font-bold text-primary">{peo.score}</span>
                      <span className="text-sm text-muted-foreground">/10 Overall Score</span>
                    </div>
                    <Badge variant={peo.accreditation.includes("ESAC") ? "default" : "secondary"}>
                      {peo.accreditation}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Key Strengths */}
              <div>
                <h4 className="font-semibold mb-2">Key Strengths</h4>
                <div className="flex flex-wrap gap-2">
                  {peo.strengths.map((strength, index) => (
                    <Badge key={index} variant="outline">
                      {strength}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Pricing */}
              <div>
                <h4 className="font-semibold mb-1">Pricing Range</h4>
                <p className="text-muted-foreground">{peo.pricing}</p>
              </div>

              {/* Pros and Cons */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3 text-green-700">Pros</h4>
                  <ul className="space-y-2">
                    {peo.pros.map((pro, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-3 text-orange-700">Considerations</h4>
                  <ul className="space-y-2">
                    {peo.cons.map((con, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <div className="h-4 w-4 mt-0.5 flex-shrink-0">
                          <div className="h-2 w-2 bg-orange-500 rounded-full mt-1"></div>
                        </div>
                        <span>{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* CTA Section */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-6 text-center space-y-4">
          <h3 className="text-2xl font-bold">Ready to Find Your Perfect PEO Match?</h3>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Get personalized quotes from multiple top-rated PEO providers. 
            Our free service matches you with the best options for your specific needs.
          </p>
          <Button size="lg" className="text-lg px-8">
            <Users className="h-5 w-5 mr-2" />
            Get Free PEO Quotes
          </Button>
          <p className="text-sm text-muted-foreground">
            No obligation • Free to use • Expert guidance included
          </p>
        </CardContent>
      </Card>

      {/* Author Bio */}
      <AuthorBio />
    </div>
  );
};